<template>
  <v-layout row wrap align-content-start fill-height class="pa-4 admin-page">
    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <v-card-text>
          <span class="headline">{{ $t('patient.add') }}</span>
          <v-container grid-list-md>
            <v-form ref="form" lazy-validation>
              <v-layout wrap >
                <v-flex xs12>
                  <label v-bind:style="{ display: nameLabel, color: 'red' }">* {{ $t('common.required') }}</label>
                  <v-text-field 
                    v-model="editedItem.name"
                    :label="$t('patient.name')">
                  </v-text-field>
                </v-flex>
                <!-- 身分證欄位：由醫院配置 patient_id_required 控制顯示（慶應 false） -->
                <v-flex xs12 v-if="hospitalFeatures.patient_id_required">
                  <label v-bind:style="{ display: nationIdLabel, color: 'red' }">* {{ $t('common.required') }}</label>
                  <v-text-field
                    v-model="editedItem.nationId"
                    :label="$t('patient.nation_id')">
                  </v-text-field>
                </v-flex>
                <v-flex xs12>
                  <label v-bind:style="{ display: noLabel, color: 'red' }">* {{ $t('common.required') }}</label>
                  <v-text-field 
                    v-model="editedItem.no"
                    :label="$t('patient.medical_record')">
                  </v-text-field>
                </v-flex>
                <v-flex xs12>
                  <v-select
                    :label="$t('patient.sex')"
                    :items="[ 
                      { text: $t('common.male'), value: '1' }, 
                      { text: $t('common.female'), value: '0' } 
                    ]"
                    v-model="editedItem.gender" >
                  </v-select>
                </v-flex>
                <v-flex xs12>
                  <label v-bind:style="{ display: birthdayLabel, color: 'red' }">* {{ $t('common.required') }}</label>
                  <v-menu
                    ref="menu"
                    v-model="addCalendarMenu"
                    :close-on-content-click="false"
                    :return-value.sync="editedItem.birthday"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    full-width>
                      <v-text-field
                        v-model="editedItem.birthday"
                        slot="activator"
                        :label="$t('patient.birthday')"
                        persistent-hint
                        prepend-icon="event"
                        readonly
                        :value="editedItem.birthday">
                      </v-text-field>
                    <v-date-picker v-model="editedItem.birthday" no-title scrollable>
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="addCalendarMenu = false"
                      >
                        {{ $t('common.cancel') }}
                      </v-btn>
                      <v-btn
                        text
                        color="primary"
                        @click="$refs.menu.save(editedItem.birthday)"
                      >
                        {{ $t('common.ok') }}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-flex>
              </v-layout>                  
            </v-form>
            <v-chip class="err-msg" v-if="errorMessage" color="red" text-color="white">
              <v-icon left>info</v-icon>
              {{ errorMessage }}
            </v-chip>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" flat @click="() => this.dialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="blue darken-1" flat @click="addPatient">{{ $t('common.add') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-flex xs12 class="page-head-container mb-3">
      <v-flex xs12 class="mb-4 page-head-container">
        <h1 class="headline font-weight-light">{{ $t('patient.list') }}</h1>
      </v-flex>

      <v-flex xs12 class="search-container">
        <v-layout>
          <v-flex xs3>
            <h4>{{ $t('patient.medical_record') }}</h4>
            <v-text-field
              v-model="condition.no"
              :rules="xssRules"
              hide-details
            ></v-text-field>
          </v-flex>
          <v-flex xs3>
            <h4>{{ $t('patient.name') }}</h4>
            <search-patient-input
              @selected="selectPatient"
              :name="condition.name"
            ></search-patient-input>
          </v-flex>
          <!-- 搜尋區身分證欄位：由醫院配置控制顯示 -->
          <v-flex xs3 v-if="hospitalFeatures.patient_id_required">
            <h4>{{ $t('patient.nation_id') }}</h4>
            <v-text-field
              v-model="condition.nationId"
              :rules="xssRules"
              hide-details
            ></v-text-field>
          </v-flex>
          <v-flex xs3>
            <h4>{{ $t('common.gender') }}</h4>
            <v-select
              :items="[ 
                { text: this.$t('common.not_specific'), value: null },
                { text: 'Male', value: '1' }, 
                { text: 'Female', value: '0' } 
              ]"
              v-model="condition.gender"
            ></v-select>
          </v-flex>
          <v-flex xs2>
            <h4>{{ $t('patient.birthday') }}</h4>
            <date-picker-input
              :date="condition.birthday"
              @selected="(date) => condition.birthday = date"
            ></date-picker-input>
          </v-flex>
          <v-flex xs1>
            <v-btn
              @click="resetSearch()">
              {{ $t('common.reset') }}
            </v-btn>
          </v-flex>
          <v-flex xs1>
            <v-btn
              elevation="2"
              color="info"
              @click="search"
            > {{ $t('common.search') }}</v-btn>
          </v-flex>
        </v-layout>
      </v-flex>

      <v-flex xs12>
        <template>
          <v-data-table
            v-if="getPatientSearchResult.length > 0"
            :headers="headers"
            :items="getPatientSearchResult"
            :pagination.sync="pagination"
            hide-actions
            class="my-4"
            :disable-initial-sort=true >

            <template slot="headers" slot-scope="props">
              <tr>
                <th
                  id="patientList"
                  v-for="header in headers"
                  :key="header.text"
                  :class="['column sortable', pagination.order ? 'desc' : 'asc', header.value === pagination.title ? 'active' : '']"
                  @click="changeSort(header.value)"
                >
                  <v-icon small>arrow_upward</v-icon>
                  {{ $t(header.text) }}
                </th>
              </tr>
            </template>


            <template slot="items" slot-scope="props">
              <tr @click="clickItem(props.item)">
                <td>
                  <v-edit-dialog
                    :return-value.sync="props.item.no"
                    lazy
                    @save="updatePatient(props.item.id, 'no', tempItem.no)"
                  > {{ props.item.no }}
                    <v-text-field
                      slot="input"
                      v-model="tempItem.no"
                      label="Edit"
                      single-line
                    ></v-text-field>
                  </v-edit-dialog>
                </td>
                <td>
                  <v-edit-dialog
                    :return-value.sync="props.item.name"
                    lazy
                    @save="updatePatient(props.item.id, 'name', tempItem.name)"
                  > {{ props.item.name }}
                    <v-text-field
                      slot="input"
                      v-model="tempItem.name"
                      label="Edit"
                      single-line
                    ></v-text-field>
                  </v-edit-dialog>
                </td>
                <!-- 表格身分證欄位：由醫院配置控制顯示 -->
                <td v-if="hospitalFeatures.patient_id_required">
                  <v-edit-dialog
                    :return-value.sync="props.item.nationId"
                    lazy
                    @save="updatePatient(props.item.id, 'nationId', tempItem.nationId)"
                  > {{ props.item.nationId }}
                    <v-text-field
                      slot="input"
                      v-model="tempItem.nationId"
                      label="Edit"
                      single-line
                    ></v-text-field>
                  </v-edit-dialog>
                </td>
                <td>
                  <v-select
                    :items="[ { text: $t('common.male'), value: '1' }, { text: $t('common.female'), value: '0' } ]"
                    v-model="props.item.gender" @change="updatePatient(props.item.id,'gender',$event)"
                  ></v-select>
                </td>
                <td v-if="birthdayMenus !== null">
                  <v-menu
                    v-model="birthdayMenus[props.index]"
                    :nudge-right="40"
                    :close-on-content-click="false"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    max-width="290px"
                    min-width="290px"
                  >
                    <div slot="activator">
                      {{ props.item.birthday }}
                    </div>
                    <v-date-picker 
                      v-model="props.item.birthday" 
                      no-title 
                      @change="updatePatient(props.item.id,'birthday',props.item.birthday)">
                    <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="searchCalendarMenu = false"
                      >
                        {{ $t('common.cancel') }}
                      </v-btn>
                      <v-btn
                        text
                        color="primary"
                        @click="$refs.menu.save(props.item.birthday)"
                      >
                        {{ $t('common.ok') }}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </td>
              </tr>
            </template>
          </v-data-table>
        </template>

        <div class="text-center">
          <v-pagination
            v-model="getPatientSearchInfo.page"
            :length="getPatientSearchInfo.total_page"
            :total-visible="7"
            @input="clickPage"
          ></v-pagination>
        </div>
      </v-flex>
    </v-flex>

    <v-btn icon fixed bottom right fab color="primary" @click="() => this.dialog = true">
      <v-icon>add</v-icon>
    </v-btn>

    <v-snackbar
      v-model="snackbar"
      bottom
      :timeout="timeout"
      color="red"
    >
      {{ message }}
    </v-snackbar>
  </v-layout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import DatePickerInput from './components/DatePickerInput.vue'
import SearchPatientInput from './components/SearchPatientInput.vue';

export default {
  components: { 
    DatePickerInput,
    SearchPatientInput 
  },
    name: 'AdminPatientList',
    data() {
      return {
        addCalendarMenu: false,
        dialog: false,
        nameLabel: 'none',
        nationIdLabel: 'none',
        noLabel: 'none',
        birthdayLabel: 'none',
        pagination: {
          rowsPerPage: -1,
          title:'',
          order:false
        },
        // headers 改為 computed property（見下方 computed.headers）
        // 以支援醫院配置動態隱藏身分證欄位
        snackbar: false,
        timeout: 3000,
        birthdayMenus: null,
        tempItem:'',
        message: null,
        errorMessage: null,
        editedItem: {        
          name : 'New Patient',
          nationId : '',
          no : 'P0000000000',
          gender: '0',
          birthday: '1990-01-01'
        },
        condition: {
          page: 1,
          pgsz: 100, 
          birthday: null, 
          no: null, 
          name: null,
          nationId: null,
          gender: null,
        },
        xssRules: [
        value => {
          if ( this.condition.no == null) {
            return this.condition.no = ''
          } else {
            let xssFilter = new RegExp(/(\b)(on\S+)(\s*)=|javascript|(<\s*)(\/*)script/ig)
            this.condition.no = this.condition.no.replace(/[&><%^&"]/g, '')
            this.condition.no = (xssFilter.test(this.condition.no)) ? this.condition.no = '' : this.condition.no
            return true
          } 
        }],        
        toggleDataPicker: false,
      }
    },

    created () {
      this.search()
    },
    watch: {
      dialog() {
        this.editedItem.name = ''
        this.editedItem.nationId = ''
        this.editedItem.no = ''
        this.editedItem.gender = ''
        this.editedItem.birthday = ''
        this.$refs.form.resetValidation()
        this.errorMessage = null
      }
    },    
    computed: {
      ...mapGetters('patients', [
        'getPatientSearchResult',
        'getPatientSearchInfo'
      ]),
      ...mapGetters(['getHospitalFeatures']),
      // 醫院 features 的便捷存取（帶預設值，確保 backward compat）
      hospitalFeatures() {
        return this.getHospitalFeatures
      },
      // headers 改為 computed，依醫院配置動態過濾身分證欄位
      headers() {
        const allHeaders = [
          { text: 'patient.medical_record', align: 'left', value: 'no' },
          { text: 'patient.name', align: 'left', value: 'name' },
          { text: 'patient.nation_id', align: 'left', value: 'nationId' },
          { text: 'common.gender', align: 'left', value: 'gender' },
          { text: 'patient.birthday', align: 'left', value: 'birthday' },
        ]
        // 醫院不需要身分證時，從表頭移除
        if (!this.hospitalFeatures.patient_id_required) {
          return allHeaders.filter(h => h.value !== 'nationId')
        }
        return allHeaders
      },
    },
    methods: {
      ...mapActions('patients', [
        'searchPatients'
      ]),
      selectPatient(info) {
        if (info) {
          this.condition.no = info.no
          this.condition.gender = info.gender
          this.condition.name = info.name
          this.condition.nationId = info.nationId
        } else {
          this.condition.no = null
          this.condition.gender = null
          this.condition.name = null
          this.condition.nationId = null
        }        
      },
      search () {
        console.log('this.condition', this.condition)
        this.searchPatients(this.condition)
          .then(() => {
            this.initBirthdayMenus()
          })
      },
      resetSearch() {
        this.condition.no = null
        this.condition.gender = null
        this.condition.birthday = null
        this.condition.name = null
      },
      clickPage (page) {
        this.condition.page = page
        this.search()
      },
      changeSort (column) {
        if(column===''){return;}
        if (this.pagination.title === column) {
          this.pagination.order = !this.pagination.order
        } else {
          this.pagination.title = column
          this.pagination.order = false
        }
        this.$store.dispatch('patients/sortPatientSearchResult', {title:column,order:this.pagination.order})
      },

      initBirthdayMenus () {
        if (!this.getPatientSearchResult) {
          return
        }

        const patientsLen = this.getPatientSearchResult.length
        this.birthdayMenus = this.createArrayWithSameValue(patientsLen, false)
      },

      getGender (gender) {
        switch (Number(gender)) {
          case 0:
            return 'Female';
          case 1:
            return 'Male';
        }
      },

      createArrayWithSameValue: (size, item) => Array(size).fill(item),

      updatePatient ( patientID, field, value ) {
        
        for(let i=0;i<this.birthdayMenus.length;i++){
          this.birthdayMenus[i] = false
        }
        
        const updateObj = {
          patientID: patientID,
          field:field,
          value: value
        }

        this.$store.dispatch('patients/updateColumnById', updateObj)
         .then(res => {
            if (res && !res.success) {
              this.message = res.err.msg
              this.snackbar = true
            } else {
              this.message = null
              this.snackbar = false
            }
            this.search()
        });
      },

      async addPatient () {
        this.noLabel = this.editedItem.no == '' ? 'inline' : 'none'
        this.nameLabel = this.editedItem.name == '' ? 'inline' : 'none'
        // 身分證必填驗證：僅在醫院配置要求時才檢查
        this.nationIdLabel = this.hospitalFeatures.patient_id_required
          ? (this.editedItem.nationId == '' ? 'inline' : 'none')
          : 'none'
        this.birthdayLabel = this.editedItem.birthday == '' ? 'inline' : 'none'

        if (this.noLabel == 'none' && this.nameLabel == 'none' && this.birthdayLabel == 'none' && this.nationIdLabel == 'none') {
          try {
            await this.$store.dispatch('patients/addPatient', this.editedItem)
            .then(res => {
              if (res && !res.success) {
                this.errorMessage = res.err.msg
              } else {
                this.errorMessage = null
                this.dialog = false;
                this.search()
              }
            });
          } catch (e) {
            console.log(e)
            this.dialog = false;
          }
        }
      },
      clickItem(data){
        this.tempItem = Object.assign({}, data);
      }
      
    }
  }
</script>

<style lang="scss">
.search-container{
  button {
    margin-top: 30px;
  }
}
</style>